import { useTranslation } from "react-i18next";
import type { EditorialContent } from "@nh/shared";
import { ProductBlockHeading } from "./ProductBlockHeading";

export type ProductMetadataFields = NonNullable<EditorialContent["metadata"]>;

export interface ProductMetadataProps {
  metadata: ProductMetadataFields;
  number?: string;
}

/** Los cuatro campos son opcionales por separado: puede no haber ninguno. */
export function hasMetadata(metadata: ProductMetadataFields | undefined): boolean {
  if (!metadata) return false;
  return Boolean(metadata.content || metadata.dose || metadata.duration || metadata.cert);
}

/**
 * Los datos de un vistazo: contenido, duración, toma y certificación.
 *
 * ── La rejilla de pelo ───────────────────────────────────────────────
 * Las líneas entre celdas no son bordes: son un hueco de 1 px que deja
 * ver el fondo del contenedor. Cuatro celdas con `border` propio darían
 * líneas dobles donde se tocan —2 px en el medio y 1 px en los cantos— y
 * esa asimetría es visible aunque no se sepa nombrar. Es el mismo truco
 * de la ficha original.
 *
 * ── Dos columnas en el teléfono, cuatro desde `sm` ───────────────────
 * "60 cápsulas" a 20 px en una columna de 90 px se parte en dos líneas y
 * descuadra las cuatro celdas entre sí.
 *
 * ── Una celda sin dato no se pinta ───────────────────────────────────
 * Y si no queda ninguna, el bloque entero desaparece: un "GMP" solitario
 * al lado de tres guiones no es una ficha técnica.
 */
export function ProductMetadata({ metadata, number }: ProductMetadataProps) {
  const { t } = useTranslation();

  if (!hasMetadata(metadata)) return null;

  const cells: { label: string; value: string }[] = [
    ...(metadata.content ? [{ label: t("Contenido"), value: metadata.content }] : []),
    ...(metadata.duration ? [{ label: t("Duración"), value: metadata.duration }] : []),
    ...(metadata.dose ? [{ label: t("Toma"), value: metadata.dose }] : []),
    ...(metadata.cert ? [{ label: t("Cert."), value: metadata.cert }] : []),
  ];

  return (
    <section className="grid gap-3.5">
      <ProductBlockHeading eyebrow={t("Ficha técnica")} number={number} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cells.map((cell) => (
          <div key={cell.label} className="rounded-[22px] border border-ds-border bg-white px-4 py-[18px] shadow-ds-xs">
            <dt className="font-mono text-[9px] uppercase leading-[1.4] tracking-[0.14em] text-ds-ink-3">
              {cell.label}
            </dt>
            <dd className="mt-1.5 text-[20px] font-semibold leading-[1.15] tracking-[-0.01em] text-ds-ink">
              {cell.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
